import { ReactNode, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { createStripeCheckoutSession } from '../services/stripe/stripeApi';
import { getStripe } from '../services/stripe/stripeClient';
import { DatabaseSoundPackModel } from '../services/DatabaseSoundPack';
import { DatabaseSettingsModel } from '../services/DatabaseSettings';
import { SubscriptionInterval } from '../services/DatabaseUser';
import Button from './ui/Button';
import { LANDING_TERMS_URL } from '../constants/urls';

// The subscription buy step: one focused card inside the normal site shell.
// Headline, Monthly/Yearly toggle (yearly by default), price + billed note, one
// feature line, renewal terms, "Check out".
// Already-subscribed users are sent to /account, where subscription management
// lives.

function CheckIcon() {
    return (
        <svg viewBox="0 0 16 12" className="h-3 w-4 flex-none" fill="none" aria-hidden>
            <path
                d="M1 6l4.5 4.5 8-9"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

// Faint hairline used between the card's rows (rgba(203,203,203,0.36)).
function Rule() {
    return <div className="h-px bg-[rgba(203,203,203,0.36)]" />;
}

// One segment of the Monthly/Yearly pill toggle.
function PlanToggleButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={`rounded-[7px] px-[26px] py-2 text-[15px] font-medium transition-colors ${
                selected ? 'bg-[#4D4D4B] text-white' : 'bg-transparent text-subtle hover:text-ink-secondary'
            }`}
        >
            {children}
        </button>
    );
}

function SubscribePage() {
    const { userData } = useUser();
    const { settings } = useAppSettings();
    const [confirming, setConfirming] = useState(false);
    const [plan, setPlan] = useState<SubscriptionInterval>('year');

    // Already subscribed — nothing to buy; subscription management is on Account.
    if (userData?.subscription?.status === 'active') {
        return <Navigate to="/account" replace />;
    }

    const format = DatabaseSoundPackModel.formatPrice;
    const monthlyPrice = settings?.subscriptionPrice ?? '9.99';
    const yearlyPrice = settings?.yearlySubscriptionPrice ?? '95.88';
    const yearlyMonthlyPrice = DatabaseSettingsModel.yearlyMonthlyPrice(settings) ?? '7.99';
    const yearlySavingPercent = Math.round((1 - parseFloat(yearlyPrice) / (parseFloat(monthlyPrice) * 12)) * 100);

    const isYearly = plan === 'year';
    const priceId = isYearly ? settings?.yearlySubscriptionPriceId : settings?.subscriptionPriceId;
    const priceLabel = format(isYearly ? yearlyMonthlyPrice : monthlyPrice);
    const billedNote = isYearly ? `${format(yearlyPrice)} billed annually` : 'billed monthly';
    const renewalTerms = isYearly ? `${format(yearlyPrice)} every 12 months` : `${format(monthlyPrice)} every month`;

    const handleCheckout = async () => {
        if (!priceId) {
            console.error(`No ${isYearly ? 'yearly' : 'monthly'} subscription price ID configured in settings/settings.`);
            return;
        }

        setConfirming(true);
        try {
            const sessionId = await createStripeCheckoutSession(priceId, 'subscription');
            const stripe = await getStripe();
            if (stripe) {
                const { error } = await stripe.redirectToCheckout({ sessionId });
                if (error) console.error('Stripe redirect error:', error);
            }
            // Redirect either navigated away or failed — re-enable the button.
            setConfirming(false);
        } catch (error) {
            console.error('Error starting subscription checkout:', error);
            setConfirming(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-[528px]">
            <div className="flex flex-col rounded-[20px] border border-[#E7E7E7] bg-surface p-7 sm:p-10">
                <h1 className="text-[28px] font-extrabold leading-[1.18] tracking-[-0.02em] text-ink-secondary sm:text-[34px]">
                    <span className="text-ghost">Subscribe to</span>
                    <br />
                    All-Access<span className="text-alert">.</span>
                </h1>

                <div className="mt-7 h-px bg-ink-secondary" />

                <div className="flex items-center justify-between gap-3 py-[18px]">
                    <div className="inline-flex rounded-[10px] border border-[#CBCBCB] p-[3px]">
                        <PlanToggleButton selected={!isYearly} onClick={() => setPlan('month')}>
                            Monthly
                        </PlanToggleButton>
                        <PlanToggleButton selected={isYearly} onClick={() => setPlan('year')}>
                            Yearly
                        </PlanToggleButton>
                    </div>
                    {isYearly && yearlySavingPercent > 0 && (
                        <span className="rounded-[6px] bg-[#ECEBE6] px-3 py-[5px] text-[12px] font-medium text-[#4D4D4B]">
                            −{yearlySavingPercent}%
                        </span>
                    )}
                </div>

                <Rule />

                <div className="flex flex-wrap items-baseline justify-between gap-3 pt-6 pb-[30px]">
                    <div className="flex items-baseline gap-2.5">
                        <span className="text-[44px] font-extrabold leading-none tracking-[-0.03em] text-ink-secondary sm:text-[58px]">
                            {priceLabel}
                        </span>
                        <span className="text-[20px] text-ghost">/mo</span>
                    </div>
                    <span className="text-[15px] text-[#5F5F5F]">{billedNote}</span>
                </div>

                <Rule />

                <div className="flex items-center gap-3.5 py-5 text-ink-secondary">
                    <CheckIcon />
                    <span className="text-item-sm">Unlimited access to Organic Electronic packs</span>
                </div>

                <Rule />

                <p className="mt-[22px] text-r-sub leading-[1.7] text-[#6E6E6E]">
                    Renews automatically at {renewalTerms} until you cancel. Cancel anytime online on
                    your Account page, effective at the end of your billing period.
                </p>

                <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={handleCheckout}
                    disabled={confirming || !priceId}
                    className="mt-[34px] text-item-sm"
                >
                    {confirming ? 'One moment…' : 'Check out'}
                </Button>

                <p className="mt-3.5 text-center text-r-cap text-subtle">Secure payment via Stripe</p>

                <p className="mt-[18px] text-r-cap text-subtle">
                    By subscribing you agree to the renewal terms above and the{' '}
                    <a href={LANDING_TERMS_URL} className="text-ink-secondary">
                        Terms of Use
                    </a>
                    .
                </p>
            </div>
        </div>
    );
}

export default SubscribePage;
